/**
 * AttentionTable — top-10 list of items that need the director's
 * attention right now. Each row click-throughs to the source record
 * (CAPA, deviation, OOS, or workflow). Critical/breach rows get a
 * red border-start indicator.
 */

import type { AttentionItem } from './useQualityDashboard';

const KIND_LABEL: Record<AttentionItem['kind'], string> = {
  CAPA: 'CAPA',
  DEVIATION: 'Deviation',
  OOS: 'OOS',
  WORKFLOW: 'Workflow',
};

const CRIT_STYLES: Record<AttentionItem['criticality'], { bg: string; fg: string }> = {
  CRITICAL: { bg: 'var(--color-status-error-bg, #FEF2F2)', fg: 'var(--color-status-error, #B91C1C)' },
  BREACH:   { bg: 'var(--color-status-warning-bg, #FFFBEB)', fg: 'var(--color-status-warning, #B45309)' },
  MAJOR:    { bg: 'var(--color-status-warning-bg, #FFFBEB)', fg: 'var(--color-status-warning, #B45309)' },
  MINOR:    { bg: 'var(--color-bg-surface, #F3F4F6)', fg: 'var(--color-text-secondary, #4B5563)' },
};

export interface AttentionTableProps {
  items: AttentionItem[];
  onOpen?: (item: AttentionItem) => void;
}

export function AttentionTable({ items, onOpen }: AttentionTableProps) {
  if (items.length === 0) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-tertiary, #6B7989)', fontSize: 13 }}>
        Nothing needs your attention right now. ✅
      </div>
    );
  }
  return (
    <>
      <style>{`
        .qd-attention-list button { transition: background 120ms ease; }
        .qd-attention-list button:hover  { background: var(--color-bg-surface, #F3F4F6) !important; }
        .qd-attention-list button:focus-visible { outline: 2px solid var(--color-status-info, #2563EB); outline-offset: -2px; }
      `}</style>
    <ul className="qd-attention-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
      {items.map((it, idx) => {
        const crit = CRIT_STYLES[it.criticality];
        return (
          <li key={`${it.kind}-${it.id}-${idx}`}>
            <button
              type="button"
              onClick={() => (onOpen ? onOpen(it) : window.location.assign(it.link))}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                width: '100%', textAlign: 'start', cursor: 'pointer',
                background: 'transparent', border: 'none',
                borderBottom: idx < items.length - 1 ? '1px solid var(--color-border-subtle, #E5E7EB)' : 'none',
                borderInlineStart: (it.criticality === 'CRITICAL' || it.criticality === 'BREACH')
                  ? '3px solid var(--color-status-error, #B91C1C)'
                  : '3px solid transparent',
              }}
            >
              <span style={{
                padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                background: 'var(--color-bg-surface, #F3F4F6)', color: 'var(--color-text-secondary, #4B5563)',
                minWidth: 60, textAlign: 'center',
              }}>
                {KIND_LABEL[it.kind]}
              </span>
              <span style={{
                padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600,
                background: crit.bg, color: crit.fg, minWidth: 64, textAlign: 'center',
              }}>
                {it.criticality}
              </span>
              <span style={{ fontWeight: 600, color: 'var(--color-text-primary, #111827)' }}>{it.label}</span>
              <span style={{ flex: 1, color: 'var(--color-text-secondary, #4B5563)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.title}
              </span>
              {it.age_days !== null && (
                <span style={{ fontSize: 12, color: 'var(--color-text-tertiary, #6B7989)', fontVariantNumeric: 'tabular-nums' }}>
                  {it.age_days}d old
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
    </>
  );
}
